import * as React from 'react';
import { useParams } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import ClientFormDialog from './ClientFormDialog';
import { clientInitials } from './Clients';
import { SectionCard, StatCard, EmptyState, currency, number } from './ui';
import ClientBalance from './ClientBalance';
import { TRUE_COLORS } from '../theme';

const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', online: 'Pagado en línea', balance: 'Saldo',
};

const CONSENT_SOURCES: Record<string, string> = { pos: 'en caja', panel: 'en el panel', web: 'en la web', whatsapp: 'por WhatsApp' };

/** Consent to receive promotions (privacy law): shown with when and where it was given; the business withdraws it
 * when the customer asks, and only turns it on when the customer asked for it. */
const PromotionsConsent = ({ client, onChange }: { client: any; onChange: (optIn: boolean) => Promise<void> }) => {
  const [saving, setSaving] = React.useState(false);
  const optIn = !!client?.marketing_opt_in;
  const since = optIn && client?.opt_in_at
    ? `Desde ${dayjs(client.opt_in_at).format('D MMM YYYY')}${CONSENT_SOURCES[client.opt_in_source] ? ` · ${CONSENT_SOURCES[client.opt_in_source]}` : ''}`
    : 'No ha aceptado. Actívalo solo si el cliente lo pidió.';
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Box sx={{ color: TRUE_COLORS.secondary, mt: 0.25, '& svg': { fontSize: 20 } }}><CampaignOutlinedIcon /></Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Promociones por WhatsApp</Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{optIn ? 'Acepta' : 'No acepta'}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{since}</Typography>
      </Box>
      <Switch
        checked={optIn}
        disabled={!client || saving}
        inputProps={{ 'aria-label': 'Acepta promociones por WhatsApp' }}
        onChange={async e => { setSaving(true); try { await onChange(e.target.checked); } finally { setSaving(false); } }}
      />
    </Stack>
  );
};

const ContactRow = ({ icon, label, value, href }: { icon: React.ReactNode; label: string; value?: string | null; href?: string }) => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
    <Box sx={{ color: TRUE_COLORS.secondary, mt: 0.25, '& svg': { fontSize: 20 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{label}</Typography>
      {value ? (
        <Typography
          component={href ? 'a' : 'p'}
          href={href}
          sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary', textDecoration: 'none', wordBreak: 'break-word' }}
        >
          {value}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Sin registrar</Typography>
      )}
    </Box>
  </Stack>
);

const ClientDetail = () => {
  const { id } = useParams();
  const axios = useAxios();

  const [client, setClient] = React.useState<any>(null);
  const [summary, setSummary] = React.useState<any>(null);
  const [receipts, setReceipts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [editing, setEditing] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const [c, s, r] = await Promise.allSettled([
        axios.get(`api/clients/${id}/`),
        axios.get('api/receipts/summary/', { params: { client: id } }),
        axios.get('api/receipts/', { params: { client: id } }),
      ]);
      if (c.status === 'fulfilled') setClient(c.value.data); else setNotFound(true);
      if (s.status === 'fulfilled') setSummary(s.value.data?.[0] ?? null);
      if (r.status === 'fulfilled') {
        setReceipts([...(r.value.data ?? [])].sort((a, b) => dayjs(b.purchase_date).diff(dayjs(a.purchase_date))));
      }
      setLoading(false);
    })();
  }, [axios, id]);

  if (notFound) {
    return (
      <PageShell back={{ to: '/admin/clients', label: 'Clientes' }}>
        <EmptyState title="Cliente no encontrado" description="Puede que haya sido eliminado o pertenezca a otra organización." />
      </PageShell>
    );
  }

  const fullName = client ? `${client.name} ${client.lastname}`.trim() : '';
  const lastPurchase = receipts[0]?.purchase_date;

  return (
    <PageShell
      back={{ to: '/admin/clients', label: 'Clientes' }}
      title={
        loading ? <Skeleton width={260} /> : (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Avatar sx={{ width: 48, height: 48, fontWeight: 700, bgcolor: TRUE_COLORS.primary }}>{clientInitials(client)}</Avatar>
            <span>{fullName}</span>
          </Stack>
        )
      }
      subtitle={client?.registry_date ? `Cliente desde ${dayjs(client.registry_date).format('D [de] MMMM [de] YYYY')}` : undefined}
      actions={
        <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)} disabled={!client}>
          Editar
        </Button>
      }
    >
      <Grid container spacing={3}>
        {[
          { label: 'Visitas', value: number(summary?.total_count), icon: <StorefrontOutlinedIcon /> },
          { label: 'Total gastado', value: currency(summary?.total_amount), icon: <PaymentsOutlinedIcon /> },
          { label: 'Ticket promedio', value: currency(summary?.avg), icon: <ShoppingBasketOutlinedIcon /> },
          { label: 'Última compra', value: lastPurchase ? dayjs(lastPurchase).fromNow() : '—', icon: <EventOutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={loading} />
          </Grid>
        ))}

        <Grid size={{ xs: 12, md: 4 }}>
          <SectionCard title="Contacto">
            <Stack spacing={2.5}>
              <ContactRow icon={<EmailOutlinedIcon />} label="Correo" value={client?.email} href={client?.email ? `mailto:${client.email}` : undefined} />
              <ContactRow icon={<PhoneOutlinedIcon />} label="Teléfono" value={client?.phone} href={client?.phone ? `tel:${client.phone}` : undefined} />
              <ContactRow icon={<PlaceOutlinedIcon />} label="Dirección" value={client?.address} />
              <PromotionsConsent
                client={client}
                onChange={async optIn => {
                  const { data } = await axios.put(`api/clients/${id}/`, {
                    name: client.name, lastname: client.lastname, email: client.email, phone: client.phone, address: client.address,
                    marketing_opt_in: optIn, opt_in_source: optIn ? 'panel' : client.opt_in_source,
                  });
                  setClient(data);
                }}
              />
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <SectionCard title="Historial de compras" subtitle={`${number(receipts.length)} ${receipts.length === 1 ? 'compra' : 'compras'}`} noPadding>
            {receipts.length === 0 ? (
              <EmptyState compact icon={<ReceiptLongOutlinedIcon />} title="Sin compras registradas" description="Las ventas asociadas a este cliente en TRUE POS aparecerán aquí." />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Sucursal</TableCell>
                      <TableCell>Artículos</TableCell>
                      <TableCell>Pago</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receipts.slice(0, 50).map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.purchase_date).format('D MMM YYYY, HH:mm')}</TableCell>
                        <TableCell>{r.checkout?.location?.name ?? '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography noWrap sx={{ fontSize: '0.8125rem' }}>
                            {(r.items ?? []).map((i: any) => i.product?.name).filter(Boolean).join(', ') || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.payment ? <Chip size="small" label={PAYMENT_LABELS[r.payment] ?? r.payment} /> : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </SectionCard>
        </Grid>

        {id && <ClientBalance clientId={id} />}
      </Grid>

      <ClientFormDialog open={editing} client={client} onClose={() => setEditing(false)} onSaved={setClient} />
    </PageShell>
  );
};

export default ClientDetail;
